"use client"

import React, { useState, useEffect } from "react"
import { fetchAcademyData } from "../comman/Academy.js"
import { fetchBatchesForCoach } from "../comman/Student.js"

// Inline component definitions
const Card = ({ className, children }) => (
  <div className={`bg-white rounded-lg shadow-xl overflow-hidden ${className}`}>{children}</div>
)

const CardHeader = ({ className, children }) => <div className={`p-6 ${className}`}>{children}</div>

const CardTitle = ({ className, children }) => <h2 className={`text-2xl font-bold ${className}`}>{children}</h2>

const CardContent = ({ className, children }) => <div className={`p-6 ${className}`}>{children}</div>

const Input = React.forwardRef(({ className, ...props }, ref) => (
  <input className={`w-full p-2 border border-gray-300 rounded-md ${className}`} ref={ref} {...props} />
))

const Label = ({ className, children, ...props }) => (
  <label className={`block text-sm font-medium mb-1 ${className}`} {...props}>
    {children}
  </label>
)

const Select = ({ children, ...props }) => (
  <select className="w-full p-2 border border-gray-300 rounded-md" {...props}>
    {children}
  </select>
)

const Button = ({ className, children, ...props }) => (
  <button className={`px-4 py-2 rounded-md ${className}`} {...props}>
    {children}
  </button>
)

const CoachModal = ({ isModalOpen, setIsModalOpen, coachData, handleAddCoach, handleUpdateCoach }) => {
  const [newCoach, setNewCoach] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    sport: "",
    academy: "",
    id: "",
    batchId: "",
    coachAcademyId: "",
  })

  const [academies, setAcademies] = useState([])
  const [batches, setBatches] = useState([])
  const [selectedAcademy, setSelectedAcademy] = useState("")
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const sports = ["All", "Football", "Basketball", "Cricket", "Badminton", "Tennis", "Swimming"]

  useEffect(() => {
    fetchAcademy()
  }, [])

  const fetchAcademy = async () => {
    try {
      const data = await fetchAcademyData()
      setAcademies(data)
    } catch (err) {
      setError("Error fetching academies")
    } finally {
      setLoading(false)
    }
  }

  const fetchBatch = async (academyId, sport) => {
    try {
      const response = await fetchBatchesForCoach(academyId, sport)
      setBatches(response)
    } catch (error) {
      console.error(error.message)
    }
  }

  const generateCoachAcademyId = () => {
    const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ"
    const randomLetters =
      letters.charAt(Math.floor(Math.random() * letters.length)) +
      letters.charAt(Math.floor(Math.random() * letters.length)) +
      letters.charAt(Math.floor(Math.random() * letters.length))
    const randomDigits = Math.floor(100 + Math.random() * 900)
    return `${randomLetters}${randomDigits}`
  }

  const handleChange = (name, value) => {
    setNewCoach((prevData) => {
      const updatedData = { ...prevData, [name]: value }

      if (name === "academy") {
        setSelectedAcademy(value)
        const selectedAcademyObj = academies.find((a) => a.name === value)
        if (selectedAcademyObj) {
          fetchBatch(selectedAcademyObj._id, updatedData.sport)
        }
      }

      if (name === "sport") {
        const selectedAcademyObj = academies.find((a) => a.name === updatedData.academy)
        if (selectedAcademyObj) {
          fetchBatch(selectedAcademyObj._id, value)
        }
      }

      if (name === "batchId" && value) {
        updatedData.coachAcademyId = generateCoachAcademyId()
      }

      return updatedData
    })
  }

  const resetForm = () => {
    setNewCoach({
      name: "",
      phone: "",
      confirmPassword: "",
      sport: "",
      academy: "",
      id: "",
      batchId: "",
      coachAcademyId: "",
    })
  }

  useEffect(() => {
    if (coachData) {
      setNewCoach({
        name: coachData.name || "",
        phone: coachData.phone || "",
        sport: coachData.profile.sport || "All",
        academy: coachData.teachesAtAcademyId[0]?.name || "",
        id: coachData._id,
        batchId: coachData.batchId || "",
        coachAcademyId: coachData.coachacademyid || "",
      })
    }
  }, [coachData])

  const handleSubmit = () => {
    if (coachData) {
      handleUpdateCoach(newCoach)
    } else {
      handleAddCoach(newCoach)
    }
    setIsModalOpen(false)
  }

  if (!isModalOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="bg-[#12143F] text-white">
          <CardTitle>{coachData ? "Edit Coach" : "Add New Coach"}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input id="name" value={newCoach.name} onChange={(e) => handleChange("name", e.target.value)} required />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phoneNumber">Phone Number</Label>
            <Input
              id="phoneNumber"
              type="tel"
              value={newCoach.phone}
              onChange={(e) => handleChange("phone", e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="sport">Sport</Label>
            <Select id="sport" value={newCoach.sport} onChange={(e) => handleChange("sport", e.target.value)}>
              <option value="">Select a sport</option>
              {sports.map((sport) => (
                <option key={sport} value={sport}>
                  {sport}
                </option>
              ))}
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="academy">Academy</Label>
            <Select id="academy" value={newCoach.academy} onChange={(e) => handleChange("academy", e.target.value)}>
              <option value="">Select an academy</option>
              {academies.map((academy) => (
                <option key={academy._id} value={academy.name}>
                  {academy.name}
                </option>
              ))}
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="batchId">Batch</Label>
            <Select id="batchId" value={newCoach.batchId} onChange={(e) => handleChange("batchId", e.target.value)}>
              <option value="">Select a batch</option>
              {batches.map((batch) => (
                <option key={batch._id} value={batch._id}>
                  {batch.batchName}
                </option>
              ))}
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="coachAcademyId">Coach Academy ID</Label>
            <Input id="coachAcademyId" value={newCoach.coachAcademyId} readOnly className="bg-gray-100" />
          </div>

          <div className="flex justify-end space-x-4 mt-6">
            <Button
              onClick={() => {
                setIsModalOpen(false)
                resetForm()
              }}
              className="bg-gray-300 text-gray-800 hover:bg-gray-400"
            >
              Cancel
            </Button>
            <Button onClick={handleSubmit} className="bg-[#12143F] text-white hover:bg-[#1a1c5f]">
              {coachData ? "Update Coach" : "Add Coach"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default CoachModal

